import { RootState } from '@/store/store';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAppFilters } from '@/hooks/shared';

type CustomFilter<T> = (item: T, searchValue: string) => boolean;

export function useFilteredList<T extends Record<string, any>>(
  items: T[] | undefined,
  searchKeys: (keyof T)[],
  customFilter?: CustomFilter<T>
) {
  const [searchParams] = useSearchParams();
  const { assetsPreview, assetsType } = useAppFilters();
  const { searchByAssetType } = useSelector((state: RootState) => state.assetsPreview);

  // Use asset-type-specific search value to avoid cross-asset-type search pollution
  const searchValue = assetsPreview === 'assets' && assetsType ? (searchByAssetType[assetsType] || '') : '';

  const filtered = useMemo(() => {
    if (!items) return [];

    if (!searchValue) return items;

    return items.filter((item) => {
      if (customFilter) return customFilter(item, searchValue);

      // default filter: check all searchKeys
      return searchKeys.some((key) => String(item[key]).toLowerCase().includes(searchValue));
    });
  }, [items, searchParams, searchKeys, customFilter, searchValue]);

  return filtered;
}
